/* eslint-disable */
import React, { useEffect, useState } from "react";
import styles from "./CandidatePipeline.module.scss";
import { PiFunnelBold } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/Common/Loader/Loader";

import {
  fetchRegisteredCandidates,
  fetchCanLeads,
  setCommonFilters,
  resetCommonFilters,
  setRegisteredFilters,
  resetRegisteredFilters,
  setCanLeadFilters,
  resetCanLeadFilters,
  setRegisteredPage,
  setCanLeadPage,
  selectRegistered,
  selectCanLeads,
  selectCommonFilters,
  selectCommonHasFilter,
  fetchKeySkills,
  selectKeySkills,
} from "../../../redux-store/slices/candidateKanbanSlice";

import { decryptedAdminID } from "../../../utility";
import CommonFilterOffcanvas from "./Filter/CommonFilterOffcanvas";
import LeadFilterOffcanvas from "./Filter/LeadFilterOffcanvas";
import KanbanColumn from "../../../components/Kanban/KanbanBoardLeadRegister";
import {
  GetdiplomaCourses,
  GetITICourses,
  getJobFilterOptions,
  GetPGCourses,
  GetUGCourses,
} from "../../../apiServices";
import RegisteredFilterOffcanvas from "./Filter/RegisteredFilterOffcanvas";

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

const CandidatePipeline = ({ onSelectCandidate, onSelectLeadCandidate }) => {
  const dispatch = useDispatch();
  const adminID = Number(decryptedAdminID());

  const regState = useSelector(selectRegistered);
  const leadState = useSelector(selectCanLeads);
  const commonFilters = useSelector(selectCommonFilters);
  const hasFilter = useSelector(selectCommonHasFilter);
  const keySkillsState = useSelector(selectKeySkills);

  // ── Offcanvas visibility ──
  const [openCommonFilter, setOpenCommonFilter] = useState(false);
  const [openLeadFilter, setOpenLeadFilter] = useState(false);
  const [openRegFilter, setOpenRegFilter] = useState(false);

  const [isRefreshLead, setIsRefreshLead] = useState(false);
  const [isRefreshReg, setIsRefreshReg] = useState(false);
  const [adminList, setAdminList] = useState([]);

  const adminDetails = useSelector((state) => state.adminDetails);
  const isSuperAdmin = adminDetails.roleID == 1;
  const isTeamLeader = localStorage.getItem("teamLeader") == "true";

  const [filterOptions, setFilterOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // ── Load Admin List ──
  useEffect(() => {
    const load = () =>
      setAdminList(JSON.parse(localStorage.getItem("AdminList") || "[]"));
    load();
    window.addEventListener("storage", load);
    return () => window.removeEventListener("storage", load);
  }, []);

  // ── Load Key Skills once ──
  useEffect(() => {
    if (!keySkillsState.loaded && !keySkillsState.loading) {
      dispatch(fetchKeySkills());
    }
  }, []);


// ✅ Only re-fetch when loaded becomes false (filter reset / apply sets this)
useEffect(() => {
  if (!leadState.loaded) {
    dispatch(fetchCanLeads({
      ...leadState.filters,
      page: leadState.page,
      limit: leadState.limit,
    }));
  }
}, [leadState.loaded]);

useEffect(() => {
  if (!regState.loaded) {
    dispatch(fetchRegisteredCandidates({
      ...regState.filters,
      page: regState.page,
      size: regState.limit,
    }));
  }
}, [regState.loaded]);

  useEffect(() => {
    if (filterOptions) return; // ✅ prevent multiple calls

    const fetchOptions = async () => {
      try {
        const [jobData, iti, diploma, ug, pg] = await Promise.all([
          getJobFilterOptions(),
          GetITICourses(),
          GetdiplomaCourses(),
          GetUGCourses(),
          GetPGCourses(),
        ]);

        // 🔥 FIXED mapping for jobData
        const mapData = (arr) =>
          (arr || []).map((item, index) => ({
            id: item.id ?? index, // fallback if no id
            label: item.options || "", // ✅ correct key
          }));

        const formatted = {
          jobCategory: mapData(
            jobData.filter((x) => x.category === "job category"),
          ),
          prefLocation: mapData(jobData.filter((x) => x.category === "city")),
          prefArea: mapData(jobData.filter((x) => x.category === "areas")),
          qualification: mapData(
            jobData.filter((x) => x.category === "qualification"),
          ),
        };

        // 🔥 FIXED specification mapping
        const specification = [iti, diploma, ug, pg]
          .map((res) => res?.results || [])
          .flat()
          .map((item, index) => ({
            id: item.id ?? index,
            label: item.courses || "", // ✅ correct key
          }));

        setFilterOptions({
          ...formatted,
          specification,
        });
      } catch (err) {
        console.error("Filter API Error:", err);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  // ASSIGN BUILDER
// const buildAssign = (cf) => ({
//   assign_to: cf.assign_to ?? adminID,
//   onlyMe:
//     cf.onlyMe != null
//       ? cf.onlyMe
//       : isSuperAdmin
//       ? false
//       : true,  // teamleader + normal admin → always onlyMe true
// });

const buildAssign = (cf) => {
  const loggedId = Number(decryptedAdminID());
  return {
    assign_to: cf.assign_to ?? loggedId,  // ✅ never null
    onlyMe: cf.onlyMe ?? false,
  };
};

  // FILTER MAPPING  (shared → API-specific keys)
  const buildLeadFilters = (cf) => ({
    mobile_number: cf.mobile_number,
    first_name: cf.first_name,
    job_category: cf.job_category,
    fromSource: cf.source ?? cf.fromSource, // support both shapes
    pref_location: cf.pref_location,
    pref_area: cf.pref_area,
    exp_from: cf.exp_from,
    exp_to: cf.exp_to,
    created_from: cf.created_from,
    created_to: cf.created_to,
    sub_stage_date_from: cf.sub_stage_date_from,
    sub_stage_date_to: cf.sub_stage_date_to,
    lead_deactive: cf.lead_deactive,
    alreadyRegister: cf.alreadyRegister,
    registeredMoved: cf.registeredMoved,
    current_sub_stage_id: cf.current_sub_stage_id,
    current_stage_id: cf.current_stage_id,
  })

  const buildRegFilters = (cf) => ({
    mobile_number: cf.mobile_number,
    first_name: cf.first_name,
    job_category: cf.job_category,
    from_source: cf.source ?? cf.from_source, 
    skill: cf.skill,
    pref_location: cf.pref_location,
    pref_area: cf.pref_area,
    exp_from: cf.exp_from,
    exp_to: cf.exp_to,
    created_from: cf.created_from,
    created_to: cf.created_to,
    entry_from: cf.entry_from,
    entry_to: cf.entry_to,
    sub_stage_date_from: cf.sub_stage_date_from,
    sub_stage_date_to: cf.sub_stage_date_to,
    deActivated: cf.deActivated,
    current_sub_stage_id: cf.current_sub_stage_id,
    qualification: cf.qualification,
    specification: cf.specification,
  });

  // INITIAL LOAD
  // useEffect(() => {
  //   if (!adminList.length) return;

  //   const a = buildAssign(commonFilters);
  //   console.log(a,"Data for assign to");
    

  //   dispatch(
  //     fetchCanLeads({
  //       ...buildLeadFilters(commonFilters),
  //       ...a,
  //       page: 1,
  //       limit: leadState.limit,
  //     }),
  //   );

  //   dispatch(
  //     fetchRegisteredCandidates({
  //       ...buildRegFilters(commonFilters),
  //       ...a,
  //       page: 1,
  //       size: regState.limit,
  //     }),
  //   );
  // }, [adminList]);

  // COMMON FILTER  (affects both columns)
  const handleCommonApply = (form) => {
    dispatch(setCommonFilters(form));

    const a = buildAssign(form);
    console.log(a,"apply a filter");
    

    dispatch(
      fetchCanLeads({
        ...buildLeadFilters(form),
        ...a,
        page: 1,
        limit: leadState.limit,
      }),
    );

    dispatch(
      fetchRegisteredCandidates({
        ...buildRegFilters(form),
        ...a,
        page: 1,
        size: regState.limit,
      }),
    );
  };

  const handleCommonReset = () => {
    dispatch(resetCommonFilters());

    const a = { assign_to: adminID, onlyMe: isSuperAdmin ? false : true };
    // console.log(a,"Data reseted");
    

    dispatch(
      fetchCanLeads({
        ...buildLeadFilters({}),
        ...a,
        page: 1,
        limit: leadState.limit,
      }),
    );

    dispatch(
      fetchRegisteredCandidates({
        ...buildRegFilters({}),
        ...a,
        page: 1,
        size: regState.limit,
      }),
    );
  };

  // ─────────────────────────────────────────────
  // LEAD-ONLY FILTER
  // ─────────────────────────────────────────────
  const handleLeadApply = (form) => {
    // form already has fromSource mapped by the offcanvas
    dispatch(setCanLeadFilters(form));

    const a = buildAssign(form);

    dispatch(
      fetchCanLeads({
        ...buildLeadFilters(form),
        ...a,
        page: 1,
        limit: leadState.limit,
      }),
    );
  };

  const handleLeadReset = () => {
    dispatch(resetCanLeadFilters());

    const a = buildAssign(commonFilters);

    dispatch(
      fetchCanLeads({
        ...buildLeadFilters(commonFilters),
        ...a,
        page: 1,
        limit: leadState.limit,
      }),
    );
  };

  // ─────────────────────────────────────────────
  // REGISTERED-ONLY FILTER
  // ─────────────────────────────────────────────
  const handleRegApply = (form) => {
    // form already has from_source mapped by the offcanvas
    dispatch(setRegisteredFilters(form));

    const a = buildAssign(form);

    dispatch(
      fetchRegisteredCandidates({
        ...buildRegFilters(form),
        ...a,
        page: 1,
        size: regState.limit,
      }),
    );
  };

  const handleRegReset = () => {
    dispatch(resetRegisteredFilters());

    const a = buildAssign(commonFilters);

    dispatch(
      fetchRegisteredCandidates({
        ...buildRegFilters(commonFilters),
        ...a,
        page: 1,
        size: regState.limit,
      }),
    );
  };

  // ─────────────────────────────────────────────
  // REFRESH
  // ─────────────────────────────────────────────
  const handleLeadRefresh = () => {
    setIsRefreshLead(true);
    const a = buildAssign(commonFilters);
    dispatch(
      fetchCanLeads({
        ...buildLeadFilters(leadState.filters),
        ...a,
        page: leadState.page,
        limit: leadState.limit,
      }),
    );
    setTimeout(() => setIsRefreshLead(false), 600);
  };

  const handleRegRefresh = () => {
    setIsRefreshReg(true);
    const a = buildAssign(commonFilters);
    dispatch(
      fetchRegisteredCandidates({
        ...buildRegFilters(regState.filters),
        ...a,
        page: regState.page,
        size: regState.limit,
      }),
    );
    setTimeout(() => setIsRefreshReg(false), 600);
  };

  // ─────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────
  const handleLeadPage = (_, p) => {
    dispatch(setCanLeadPage(p));
    // const a = buildAssign(commonFilters);
const a = buildAssign(leadState.filters);
    dispatch(
      fetchCanLeads({
        ...buildLeadFilters(leadState.filters),
        ...a,
        page: p,
        limit: leadState.limit,
      }),
    );
  };

  const handleRegPage = (_, p) => {
    dispatch(setRegisteredPage(p));
    const a = buildAssign(regState.filters);
    dispatch(
      fetchRegisteredCandidates({
        ...buildRegFilters(regState.filters),
        ...a,
        page: p,
        size: regState.limit,
      }),
    );
  };

  // ── Overlay click closes whichever panel is open ──
  const closeAll = () => {
    setOpenCommonFilter(false);
    setOpenLeadFilter(false);
    setOpenRegFilter(false);
  };

  const anyPanelOpen = openCommonFilter || openLeadFilter || openRegFilter;

  return (
    <div className={styles.wrapper}>
      {(leadState.loading || regState.loading) && <Loader open fullScreen />}

      {/* TOP BAR */}
      <div className={styles.topBar}>
        <span className={styles.topBarTitle}>Candidate Pipeline</span>

        {/* Common filter — affects BOTH columns */}
        <button
          className={`${styles.filterToggleBtn} ${hasFilter ? styles.filterActive : ""}`}
          onClick={() => setOpenCommonFilter(true)}
        >
          <PiFunnelBold size={16} />
          Filter
          {hasFilter && <span className={styles.filterDot} />}
        </button>
      </div>

      {/* COLUMNS */}
      <div className={styles.columnsRow}>
        <KanbanColumn
          title="Lead"
          color="#169c50"
          bgLight="#169c5020"
          data={leadState.data}
          total={leadState.total}
          loading={leadState.loading}
          page={leadState.page}
          totalPages={leadState.totalPages}
          onRefresh={handleLeadRefresh}
          onPageChange={handleLeadPage}
          onRegRefresh={handleRegRefresh}
          isRefreshing={isRefreshLead}
          activeTab="leads"
          onSelectLeadCandidate={onSelectLeadCandidate}
          // ── individual filter ──
          hasFilter={leadState.hasFilter}
          onFilterOpen={() => setOpenLeadFilter(true)}
        />

        <KanbanColumn
          title="Registered"
          color="#169c50"
          bgLight="#169c5020"
          data={regState.data}
          total={regState.total}
          loading={regState.loading}
          page={regState.page}
          totalPages={regState.totalPages}
          onRefresh={handleRegRefresh}
          onRegRefresh={handleRegRefresh}
          onPageChange={handleRegPage}
          isRefreshing={isRefreshReg}
          activeTab="registered"
          onSelectCandidate={onSelectCandidate}
          // ── individual filter ──
          hasFilter={regState.hasFilter}
          onFilterOpen={() => setOpenRegFilter(true)}
        />
      </div>

      {/* ── COMMON FILTER PANEL ── */}
      {openCommonFilter && (
        <>
          <CommonFilterOffcanvas
            filters={commonFilters}
            onApply={handleCommonApply}
            onReset={handleCommonReset}
            onClose={() => setOpenCommonFilter(false)}
            options={filterOptions} // 🔥 ADD THIS
          />
          <div className={styles.overlay} onClick={closeAll} />
        </>
      )}

      {/* ── LEAD-ONLY FILTER PANEL ── */}
      {openLeadFilter && (
        <>
          <LeadFilterOffcanvas
            filters={leadState.filters}
            onApply={handleLeadApply}
            onReset={handleLeadReset}
            onClose={() => setOpenLeadFilter(false)}
            adminList={adminList}
            options={filterOptions} // 🔥 ADD THIS
            loading={loadingOptions} // 🔥 ADD THIS
          />
          <div className={styles.overlay} onClick={closeAll} />
        </>
      )}

      {/* ── REGISTERED-ONLY FILTER PANEL ── */}
      {openRegFilter && (
        <>
          <RegisteredFilterOffcanvas
            filters={regState.filters}
            onApply={handleRegApply}
            onReset={handleRegReset}
            onClose={() => setOpenRegFilter(false)}
            adminList={adminList}
            options={filterOptions} // 🔥 ADD THIS
            loading={loadingOptions} // 🔥 ADD THIS
          />
          <div className={styles.overlay} onClick={closeAll} />
        </>
      )}
    </div>
  );
};

export default CandidatePipeline;
